import _ from 'lodash';

import {Issue, TimestampKeyTuple} from './types';

// makes sure that timestamp is within the bounds of the tuples
export function isTimestampInBounds(
  tuples: TimestampKeyTuple[],
  targetTimestamp: number
): 'before' | 'after' | 'within' | 'error' | 'at_start' | 'at_end' {
  if (tuples.length === 0) {
    return 'error';
  }

  if (targetTimestamp < tuples[0][0]) {
    return 'before';
  } else if (targetTimestamp === tuples[0][0]) {
    return 'at_start';
  } else if (targetTimestamp === tuples[tuples.length - 1][0]) {
    return 'at_end';
  } else if (targetTimestamp > tuples[tuples.length - 1][0]) {
    return 'after';
  } else {
    return 'within';
  }
}

/**
 * This function finds the X-axis position (xValue) for a given timestamp within a sorted array
 * of timestamp-xValue tuples. It interpolates between adjacent points when the exact timestamp
 * isn't found, providing smooth positioning for timeline visualizations.
 *
 * **Optimization**: Instead of searching the entire array each time, this function starts
 * searching from a provided `startIndex`. This is particularly efficient when processing
 * timestamps in chronological order, as each subsequent search can begin where the previous
 * one ended, reducing complexity from O(n²) to O(n log n) for sequential processing.
 *
 * **How it works**:
 * 1. Validates inputs and checks if timestamp is within the array bounds
 * 2. Handles exact matches at array boundaries for fast-path processing
 * 3. If target is before startIndex, falls back to full binary search
 * 4. Otherwise, performs binary search on the remaining portion of the array
 * 5. Interpolates between adjacent timestamps to calculate precise X position
 *
 * @param tuples - Sorted array of [timestamp, xValue] pairs
 * @param targetTimestamp - The timestamp to find the X position for
 * @param startIndex - Index to start searching from (optimization for sequential access)
 * @returns Object with bottomIndex and interpolated value, or undefined if timestamp is out of bounds
 *
 * @example
 * ```typescript
 * const timestamps = [[100, 0], [200, 1], [300, 2]];
 * const result = getXValueForTimestampOptimized(timestamps, 150, 0);
 * // Returns: { bottomIndex: 0, value: 0.5 } (interpolated between 100->0 and 200->1)
 * ```
 */
// Optimized version that accepts a start index to avoid array slicing
export function getXValueForTimestamp(
  tuples: TimestampKeyTuple[],
  targetTimestamp: number,
  startIndex: number = 0
):
  | {
      bottomIndex: number;
      status: 'before' | 'after' | 'at_start' | 'at_end' | 'within';
      value: number;
    }
  | undefined {
  // Validate inputs
  if (tuples.length === 0) {
    return undefined;
  }

  const target = isTimestampInBounds(tuples, targetTimestamp);
  if (target === 'error') {
    return undefined;
  }

  const effectiveStart = Math.max(0, Math.min(startIndex, tuples.length - 1));

  switch (target) {
    case 'before': {
      return {
        bottomIndex: 0,
        status: 'before',
        value: tuples[0][1],
      };
    }
    case 'after': {
      return {
        bottomIndex: tuples.length - 1,
        status: 'after',
        value: tuples[tuples.length - 1][1],
      };
    }
    case 'at_start': {
      return {
        bottomIndex: 0,
        status: 'at_start',
        value: tuples[0][1],
      };
    }
    case 'at_end': {
      return {
        bottomIndex: tuples.length - 1,
        status: 'at_end',
        value: tuples[tuples.length - 1][1],
      };
    }

    default: {
      const remainingTuples = tuples.slice(effectiveStart);
      const relativeTopIndex = findTopTimestampIndex(
        remainingTuples,
        targetTimestamp
      );
      const absoluteTopIndex = effectiveStart + relativeTopIndex;

      // Handle edge case where binary search returns length (insertion at end)
      if (absoluteTopIndex >= tuples.length) {
        const lastIndex = tuples.length - 1;
        return {
          bottomIndex: lastIndex,
          status: 'after',
          value: tuples[lastIndex][1],
        };
      }

      const bottomIndex = absoluteTopIndex - 1;

      const value = placeTimestamp(
        tuples[bottomIndex],
        tuples[absoluteTopIndex],
        targetTimestamp
      );

      return {
        bottomIndex,
        status: 'within',
        value,
      };
    }
  }
}

export function findTopTimestampIndex(
  tuples: TimestampKeyTuple[],
  targetTimestamp: number
): number {
  // implement a binary search to return the index of the first result in the tuples array where the timestamp is greater than the targetTimestamp
  // use the lodash binary search to find the first value in the array where the timestamp is greater
  const index = _.sortedIndexBy(tuples, [targetTimestamp], tuple => tuple[0]);
  return index;
}

export function placeTimestamp(
  bottomTuple: TimestampKeyTuple,
  topTuple: TimestampKeyTuple,
  targetTimestamp: number
): number {
  // first check if the targetTimestamp is exactly equal to the bottomTuple or topTuple
  if (targetTimestamp === bottomTuple[0]) {
    return bottomTuple[1];
  } else if (targetTimestamp === topTuple[0]) {
    return topTuple[1];
  }

  // now we need to place it proportionally between the bottom and top tuples
  // using the xValue
  const delta = topTuple[0] - bottomTuple[0];
  const distance = targetTimestamp - bottomTuple[0];
  const proportion = distance / delta;
  return bottomTuple[1] + (topTuple[1] - bottomTuple[1]) * proportion;
}

export function groupIssues(
  issues: Issue[],
  proximity: number = 1000 * 60 * 5 // how many minutes to group similar issues
): Issue[][] {
  const issuesByKey: Record<string, Record<number, Issue[]>> = {};
  issues.forEach(issue => {
    const keys = issuesByKey[issue.alertName];
    if (!keys) {
      issuesByKey[issue.alertName] = {};
      issuesByKey[issue.alertName][issue.timestamp] = [issue];
    } else {
      const keyTimes = Object.keys(keys).map(Number);
      const closeKey = keyTimes.find(key => {
        const delta = issue.timestamp - key;
        return delta < proximity && delta >= 0;
      });
      if (closeKey) {
        issuesByKey[issue.alertName][closeKey].push(issue);
      } else {
        issuesByKey[issue.alertName][issue.timestamp] = [issue];
      }
    }
  });
  return Object.values(issuesByKey)
    .map(issueGroup => Object.entries(issueGroup).map(e => e))
    .flat()
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(i => i[1]);
}
